import type {
  ColorScheme,
  MainButtonParams,
  MiniAppBridge,
  MiniAppUser,
  Unsubscribe,
  ViewportState,
} from './types';
import {
  createEmitter,
  createLocalStorage,
  emptyInsets,
  readLaunchParamsFromUrl,
  saveFileInMessenger,
} from './shared';

/**
 * Адаптер мини-приложений MAX.
 *
 * Мост MAX (копия `st.max.ru/js/max-web-app.js` в `public/vendor`) по API
 * близок к Telegram WebApp, но набор методов другой: нет MainButton,
 * showConfirm и requestWriteAccess, а вызовы к клиенту возвращают промисы
 * вместо колбэков. Поэтому каждый вызов проверяется на существование, а при
 * отсутствии метода используется веб-фолбэк.
 */

export type MaxWebApp = {
  initData?: string;
  initDataUnsafe?: {
    user?: {
      id: number | string;
      first_name?: string;
      firstName?: string;
      last_name?: string;
      lastName?: string;
      username?: string;
      language_code?: string;
      photo_url?: string;
    };
  };
  colorScheme?: ColorScheme;
  themeParams?: Record<string, string>;
  viewportHeight?: number;
  viewportStableHeight?: number;
  isExpanded?: boolean;
  ready?(): void;
  expand?(): void;
  close?(): void;
  onEvent?(event: string, cb: (...args: unknown[]) => void): void;
  offEvent?(event: string, cb: (...args: unknown[]) => void): void;
  BackButton?: {
    show?(): void;
    hide?(): void;
    onClick?(cb: () => void): void;
    offClick?(cb: () => void): void;
  };
  MainButton?: {
    show?(): void;
    hide?(): void;
    setParams?(params: Record<string, unknown>): void;
    onClick?(cb: () => void): void;
    offClick?(cb: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred?(style: string): unknown;
    notificationOccurred?(type: string): unknown;
    selectionChanged?(): unknown;
  };
  openLink?(url: string): void;
  openMaxLink?(url: string): void;
  share?(payload: { url?: string; text?: string }): void;
  showAlert?(message: string, cb?: () => void): void;
  showConfirm?(message: string, cb?: (ok: boolean) => void): void;
  requestWriteAccess?(cb?: (granted: boolean) => void): void;
  /**
   * Аргументы позиционные, а не объект, как у Telegram: объект MAX отклоняет
   * с `client.download_file.invalid_params`. Результат — промис, колбэка нет.
   */
  downloadFile?(url: string, file_name: string): Promise<unknown> | void;
};

declare global {
  interface Window {
    /** Объект моста MAX. Скрипт моста создаёт его на любой странице. */
    WebApp?: MaxWebApp;
    MAX?: { WebApp?: MaxWebApp };
    maxWebApp?: MaxWebApp;
  }
}

/**
 * Мост MAX, если приложение открыто в MAX.
 *
 * Скрипт моста подключается всегда и создаёт `window.WebApp` и в Telegram, и в
 * браузере. Отличает запуск из MAX только наличие подписанных данных запуска:
 * без этой проверки Telegram открывался бы с адаптером MAX.
 */
export function getMaxWebApp(): MaxWebApp | null {
  const app = window.WebApp;
  if (app?.initData) return app;
  return window.MAX?.WebApp ?? window.maxWebApp ?? null;
}

/** Вызовы моста MAX — промисы: отказ клиента не должен всплывать необработанным. */
function quiet(result: unknown): void {
  if (result instanceof Promise) result.catch(() => undefined);
}

export function createMaxBridge(app: MaxWebApp): MiniAppBridge {
  const storage = createLocalStorage('bd-max');
  const themeEmitter = createEmitter<ColorScheme>();
  const viewportEmitter = createEmitter<ViewportState>();

  const readViewport = (): ViewportState => ({
    height: app.viewportHeight ?? window.innerHeight,
    stableHeight: app.viewportStableHeight ?? app.viewportHeight ?? window.innerHeight,
    isExpanded: app.isExpanded ?? true,
  });

  app.onEvent?.('themeChanged', () => themeEmitter.emit(app.colorScheme ?? 'light'));
  app.onEvent?.('viewportChanged', () => viewportEmitter.emit(readViewport()));
  window.addEventListener('resize', () => viewportEmitter.emit(readViewport()));

  const toUser = (): MiniAppUser | null => {
    const raw = app.initDataUnsafe?.user;
    if (!raw) return null;
    return {
      id: String(raw.id),
      firstName: raw.first_name ?? raw.firstName ?? 'Пользователь',
      lastName: raw.last_name ?? raw.lastName,
      username: raw.username,
      languageCode: raw.language_code,
      photoUrl: raw.photo_url,
    };
  };

  return {
    id: 'max',
    name: 'MAX',
    isEmbedded: true,

    ready() {
      app.ready?.();
      app.expand?.();
    },
    expand() {
      app.expand?.();
    },
    close() {
      app.close?.();
    },

    // Свойство моста есть не у всех клиентов: подписанные данные запуска
    // могут прийти только в адресе, и без них вход невозможен
    getInitData: () => app.initData || readLaunchParamsFromUrl(),
    getUser: toUser,

    getColorScheme: () => app.colorScheme ?? 'light',
    onColorSchemeChange: (handler) => themeEmitter.subscribe(handler),

    getViewport: readViewport,
    onViewportChange: (handler) => viewportEmitter.subscribe(handler),
    getSafeAreaInsets: () => emptyInsets,

    setBackButtonVisible(visible) {
      if (visible) app.BackButton?.show?.();
      else app.BackButton?.hide?.();
    },
    onBackButton(handler): Unsubscribe {
      app.BackButton?.onClick?.(handler);
      return () => app.BackButton?.offClick?.(handler);
    },

    setMainButton(params: MainButtonParams | null) {
      if (!params) {
        app.MainButton?.hide?.();
        return;
      }
      app.MainButton?.setParams?.({
        text: params.text,
        is_visible: params.isVisible ?? true,
        is_active: params.isActive ?? true,
      });
      app.MainButton?.show?.();
    },
    onMainButton(handler): Unsubscribe {
      app.MainButton?.onClick?.(handler);
      return () => app.MainButton?.offClick?.(handler);
    },

    haptic: {
      impact(style = 'light') {
        quiet(app.HapticFeedback?.impactOccurred?.(style));
      },
      notification(type) {
        quiet(app.HapticFeedback?.notificationOccurred?.(type));
      },
      selection() {
        quiet(app.HapticFeedback?.selectionChanged?.());
      },
    },

    storage,

    openLink(url) {
      if (app.openLink) app.openLink(url);
      else window.open(url, '_blank', 'noopener');
    },
    openInternalLink(url) {
      if (app.openMaxLink) app.openMaxLink(url);
      else this.openLink(url);
    },
    share(payload) {
      if (app.share) {
        app.share(payload);
        return;
      }
      const text = [payload.text, payload.url].filter(Boolean).join(' ');
      void navigator.clipboard?.writeText(text);
    },

    requestWriteAccess() {
      return new Promise((resolve) => {
        if (!app.requestWriteAccess) {
          // В MAX разрешение на сервисные сообщения может выдаваться на стороне бота
          resolve(true);
          return;
        }
        app.requestWriteAccess((granted) => resolve(Boolean(granted)));
      });
    },

    /**
     * Нативная загрузка MAX принимает только https и только сразу после
     * нажатия: мост проверяет клик пользователя. Поэтому вызов идёт первым
     * делом, без ожиданий перед ним, а отказ клиента уходит в общий фолбэк.
     */
    async downloadFile({ url, fileName, blob }) {
      if (typeof app.downloadFile === 'function' && url.startsWith('https://')) {
        try {
          await app.downloadFile(url, fileName);
          return true;
        } catch {
          /* падаем в фолбэк ниже */
        }
      }
      return saveFileInMessenger({ url, fileName, blob });
    },

    showConfirm(message) {
      return new Promise((resolve) => {
        if (app.showConfirm) app.showConfirm(message, (ok) => resolve(Boolean(ok)));
        else resolve(window.confirm(message));
      });
    },
    showAlert(message) {
      return new Promise((resolve) => {
        if (app.showAlert) app.showAlert(message, () => resolve());
        else {
          window.alert(message);
          resolve();
        }
      });
    },
  };
}
